import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Users, ChevronRight, Loader2, X, GraduationCap, CalendarClock, Users2, HelpCircle, Layers } from "lucide-react";
import { openHelpGuide } from "@/lib/help";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const curriculum = useCurriculum();
  const [classes, setClasses] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", level: "Junior Infants", year: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/classes").then((r) => setClasses(r.data)).catch(() => setClasses([]));
  // Mount-once: load classes on first render only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/classes", form);
      toast.success("Class created");
      setOpen(false);
      setForm({ name: "", level: "Junior Infants", year: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const levels = curriculum?.class_levels || ["Junior Infants", "Senior Infants", "1st Class", "2nd Class"];

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 font-['Nunito']">
            {greeting()}, {user?.name?.split(" ")[0] || "Teacher"}
          </p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-slate-800 tracking-tight mt-1">
            Your Classes
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="provision-map-nav-btn"
            onClick={() => navigate("/provision-map")}
            className="bg-white border border-slate-200 hover:border-[#7FB069] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Layers size={18} /> Provision Map
          </button>
          <button
            data-testid="groups-nav-btn"
            onClick={() => navigate("/groups")}
            className="bg-white border border-slate-200 hover:border-[#9A7AA0] text-slate-700 rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
          >
            <Users2 size={18} /> Groups
          </button>
          <button
            data-testid="new-class-btn"
            onClick={() => setOpen(true)}
            className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition shadow-sm hover:shadow-md flex items-center gap-2"
          >
            <Plus size={18} /> New Class
          </button>
        </div>
      </div>

      {classes === null && (
        <div className="flex justify-center py-20 text-slate-300">
          <Loader2 className="animate-spin" />
        </div>
      )}
      {classes !== null && classes.length === 0 && (
        <EmptyClasses onCreate={() => setOpen(true)} />
      )}
      {classes !== null && classes.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {classes.map((c) => (
            <button
              key={c.id}
              data-testid={`class-card-${c.id}`}
              onClick={() => navigate(`/class/${c.id}`)}
              className="group text-left bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 hover:shadow-[0_8px_30px_rgba(42,59,71,0.09)] hover:-translate-y-0.5 transition-all"
            >
              <div className="flex items-start justify-between">
                <span className="h-11 w-11 rounded-2xl bg-[#81B29A]/15 text-[#5d8a73] flex items-center justify-center">
                  <GraduationCap size={22} />
                </span>
                <ChevronRight size={20} className="text-slate-300 group-hover:text-[#81B29A] group-hover:translate-x-0.5 transition" />
              </div>
              <h3 className="font-['Outfit'] text-xl font-semibold text-slate-800 mt-4">{c.name}</h3>
              <p className="text-sm text-slate-500 mt-0.5">{c.level}{c.year ? ` · ${c.year}` : ""}</p>
              <div className="flex items-center gap-1.5 text-sm text-slate-400 mt-4 pt-4 border-t border-slate-50">
                <Users size={15} /> {c.student_count} {c.student_count === 1 ? "student" : "students"}
                {c.overdue_review_count > 0 && (
                  <span data-testid={`overdue-badge-${c.id}`} className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                    <CalendarClock size={12} /> {c.overdue_review_count} review{c.overdue_review_count === 1 ? "" : "s"} due
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {open && (
        <Modal title="Create a class" onClose={() => setOpen(false)}>
          <form onSubmit={create} className="space-y-4">
            <Field label="Class name">
              <input
                data-testid="class-name-input"
                required
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Room 4 — Robins"
                className="input"
              />
            </Field>
            <Field label="Class level">
              <select
                data-testid="class-level-select"
                value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })}
                className="input"
              >
                {levels.map((l) => <option key={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="School year (optional)">
              <input
                data-testid="class-year-input"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                placeholder="2025 / 2026"
                className="input"
              />
            </Field>
            <button
              data-testid="save-class-btn"
              disabled={saving}
              className="w-full bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full py-3 font-semibold font-['Outfit'] transition flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={18} className="animate-spin" />} Create class
            </button>
          </form>
        </Modal>
      )}
      <style>{`.input{width:100%;background:#fff;border:1px solid #e2e8f0;border-radius:.75rem;padding:.7rem 1rem;outline:none;transition:.15s}.input:focus{border-color:#81B29A;box-shadow:0 0 0 3px #81B29A55}`}</style>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-slate-600 font-['Outfit']">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 animate-rise"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-['Outfit'] text-xl font-bold text-slate-800">{title}</h3>
          <button data-testid="modal-close-btn" onClick={onClose} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function EmptyClasses({ onCreate }) {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 p-12 text-center shadow-[0_4px_24px_rgba(42,59,71,0.04)]">
      <div className="h-16 w-16 rounded-3xl bg-[#81B29A]/15 text-[#5d8a73] flex items-center justify-center mx-auto">
        <GraduationCap size={30} />
      </div>
      <h3 className="font-['Outfit'] text-2xl font-bold text-slate-800 mt-5">No classes yet</h3>
      <p className="text-slate-500 mt-2 max-w-sm mx-auto">
        Create your first class to start adding students and tracking their progress.
      </p>
      <button
        data-testid="empty-create-class-btn"
        onClick={onCreate}
        className="mt-6 bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-6 py-2.5 font-semibold font-['Outfit'] inline-flex items-center gap-2"
      >
        <Plus size={18} /> Create a class
      </button>
      <button
        data-testid="empty-need-help-btn"
        onClick={openHelpGuide}
        className="mt-4 mx-auto flex items-center gap-1.5 text-sm font-semibold font-['Outfit'] text-[#5d8a73] hover:text-[#3f6b54] transition"
      >
        <HelpCircle size={15} /> Need a hand? See how it works
      </button>
    </div>
  );
}
